<template>
  <div class="tw-px-4">
    <!-- Loading State with Skeleton -->
    <AgendaSessionSkeleton v-if="loading" />

    <!-- Actual Content when loaded -->
    <div v-else>
      <!-- Breadcrumb -->
      <SessionBreadcrumb :session-name="session?.name" />

      <div class="tw-grid tw-grid-cols-12 tw-gap-6">
        <div class="tw-col-span-12 lg:tw-col-span-8">
          <!-- Video Container with State Overlays -->
          <div class="tw-relative">
            <!-- Session State Overlays (hidden when join prompt should show OR when joined user should see video) -->
            <SessionStateOverlay 
              v-if="!shouldShowJoinPrompt && !shouldShowVideoForJoinedUser"
              :session="session"
              :is-session-not-started="countdownTimer?.isSessionNotStarted || false"
              :is-session-about-to-start="countdownTimer?.isSessionAboutToStart || false"
              :is-session-ended="isSessionEnded"
              :should-start-video="countdownTimer?.shouldStartVideo || false"
              :session-start-countdown="countdownTimer?.sessionStartCountdown || null"
              :time-until-start="countdownTimer?.getTimeUntilStart() || 'Time not specified'"
            />

            <!-- Join Required Overlay - Shows when video should start but user hasn't joined -->
            <div v-if="shouldShowJoinPrompt" class="tw-absolute tw-inset-0 tw-z-40 tw-flex tw-items-center tw-justify-center tw-bg-gradient-to-br tw-from-primary-50 tw-to-blue-50">
              <div class="tw-text-center tw-p-8">
                <div class="tw-w-24 tw-h-24 tw-bg-primary-200 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6">
                  <icon icon="solar:user-plus-bold" class="tw-text-4xl tw-text-primary-600" />
                </div>
                <h2 class="tw-text-2xl tw-font-bold tw-text-primary-800 tw-mb-3">Join to Watch Live</h2>
                <p class="tw-text-primary-600 tw-mb-6 tw-max-w-md">
                  The session is starting now! Please join to access the live video stream and participate in discussions.
                </p>
                
                <!-- Quick Join Button -->
                <div v-if="authStore.user">
                  <a-button 
                    @click="handleQuickJoin"
                    :disabled="joinLoading"
                    intent="primary"
                    size="lg"
                    class="tw-mb-4"
                  >
                    <div class="tw-flex tw-items-center tw-justify-center">
                      <icon v-if="joinLoading" icon="solar:loading-bold" class="tw-animate-spin tw-mr-2" />
                      <icon v-else icon="solar:user-plus-bold" class="tw-mr-2" />
                      {{ joinLoading ? 'Joining...' : 'Join Session Now' }}
                    </div>
                  </a-button>
                </div>

                <!-- Login Required -->
                <div v-else class="tw-mb-4">
                  <a-button 
                    @click="handleLoginRequired"
                    intent="primary"
                    size="lg"
                  >
                    <icon icon="solar:login-bold" class="tw-mr-2" />
                    Log In to Join
                  </a-button>
                </div>

                <p class="tw-text-xs tw-text-primary-500">Session is live now - don't miss out!</p>
              </div>
            </div>

            <!-- Video Player -->
            <SessionVideoPlayer 
              :session="session"
              :should-show-iframe="shouldShowVideoPlayer"
              :show-video-header="showVideoHeader"
              :countdown="countdown"
            />
          </div>
        </div>

        <div class="tw-col-span-12 lg:tw-col-span-4">
          <!-- Session Not Started - Right Side Content -->
          <div v-if="countdownTimer?.isSessionNotStarted" class="tw-space-y-6">
            <!-- Session Registration -->
            <SessionRegistration 
              :session="session"
              :is-current-user-speaker="isCurrentUserSpeaker"
              :is-authenticated="!!authStore.user"
              @session-joined="handleSessionJoined"
              @session-left="handleSessionLeft"
              @login-required="handleLoginRequired"
            />
          </div>

          <!-- Session Ended - Right Side Content -->
          <div v-else-if="isSessionEnded" class="tw-space-y-6">
            <!-- Session Insights -->
            <SessionInsights :session="session" />

            <!-- Feedback -->
            <SessionFeedback 
              v-if="false"
              :session="session"
              @rate-session="handleRateSession"
              @write-review="handleWriteReview"
            />
          </div>

          <!-- Live Session Content -->
          <div v-else class="tw-h-[550px]">
            <!-- Chat for joined users or speakers -->
            <div v-if="session?.is_joined || isCurrentUserSpeaker" class="tw-h-full">
              <SessionChatView 
                :sessionId="sessionId" 
                :conversationId="session?.conversation?.id"
                :session="session"
              />
            </div>

            <!-- Registration prompt for non-joined users -->
            <div v-else class="tw-h-full tw-flex tw-flex-col">
              <!-- Live Session Info -->
              <div class="tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-6 tw-h-full tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center">
                <div class="tw-w-16 tw-h-16 tw-bg-red-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mb-4">
                  <icon icon="solar:tv-bold" class="tw-text-2xl tw-text-red-600" />
                </div>
                
                <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">Session is Live!</h3>
                <p class="tw-text-sm tw-text-gray-600 tw-mb-6 tw-max-w-sm">
                  This session is currently live. Join to participate in the chat and interact with other attendees.
                </p>

                <!-- Live indicators -->
                <div class="tw-flex tw-items-center tw-gap-4 tw-text-sm tw-text-gray-500 tw-mb-6">
                  <div class="tw-flex tw-items-center tw-gap-2">
                    <div class="tw-w-2 tw-h-2 tw-bg-red-500 tw-rounded-full tw-animate-pulse"></div>
                    <span>Live Now</span>
                  </div>
                  <div v-if="session?.attendees_count" class="tw-flex tw-items-center tw-gap-1">
                    <icon icon="solar:users-group-rounded-bold" class="tw-text-gray-400" />
                    <span>{{ session.attendees_count }} attending</span>
                  </div>
                </div>

                <!-- Quick Join Section -->
                <div v-if="authStore.user && !isCurrentUserSpeaker" class="tw-w-full tw-max-w-sm">
                  <a-button 
                    @click="handleQuickJoin"
                    :disabled="joinLoading"
                    intent="primary"
                    size="lg"
                    class="tw-w-full"
                  >
                    <div class="tw-flex tw-items-center tw-justify-center">
                      <icon v-if="joinLoading" icon="solar:loading-bold" class="tw-animate-spin tw-mr-2" />
                      <icon v-else icon="solar:user-plus-bold" class="tw-mr-2" />
                      {{ joinLoading ? 'Joining...' : 'Join Session' }}
                    </div>
                  </a-button>
                </div>

                <!-- Login prompt for unauthenticated users -->
                <div v-else-if="!authStore.user" class="tw-w-full tw-max-w-sm">
                  <a-button 
                    @click="handleLoginRequired"
                    intent="primary"
                    size="lg"
                    class="tw-w-full"
                  >
                    <icon icon="solar:login-bold" class="tw-mr-2" />
                    Log In to Join
                  </a-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Session Information -->
      <SessionInfo 
        :session="session"
        :is-session-not-started="countdownTimer?.isSessionNotStarted || false"
        :is-session-ended="isSessionEnded"
      />

      <!-- Speakers List -->
      <SessionSpeakersList 
        :speakers="session?.speakers"
        :loading="loading"
      />

      <!-- Attendees List -->
      <SessionAttendeesList 
        :attendees="session?.attendees"
        :loading="loading"
      />
    </div>

    <!-- Countdown Timer Component (handles all countdown logic) -->
    <SessionCountdownTimer 
      v-if="session"
      ref="countdownTimer"
      :session="session"
      :is-session-ended="isSessionEnded"
      @session-started="handleSessionStarted"
      @countdown-update="handleCountdownUpdate"
      @about-to-start="handleAboutToStart"
      @time-until-start-update="handleTimeUntilStartUpdate"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { join } from '@/api/sessions'
import SessionChatView from '@/components/messages/session/SessionChatView.vue'
import AgendaSessionSkeleton from '@/components/sessions/AgendaSessionSkeleton.vue'
import SessionBreadcrumb from '@/components/sessions/SessionBreadcrumb.vue'
import SessionVideoPlayer from '@/components/sessions/SessionVideoPlayer.vue'
import SessionCountdownTimer from '@/components/sessions/SessionCountdownTimer.vue'
import SessionStateOverlay from '@/components/sessions/SessionStateOverlay.vue'
import SessionRegistration from '@/components/sessions/SessionRegistration.vue'
import SessionInfo from '@/components/sessions/SessionInfo.vue'
import SessionSpeakersList from '@/components/sessions/SessionSpeakersList.vue'
import SessionAttendeesList from '@/components/sessions/SessionAttendeesList.vue'
import SessionInsights from '@/components/sessions/SessionInsights.vue'
import SessionFeedback from '@/components/sessions/SessionFeedback.vue'
import sessionApi from '@/api/sessions'
import useAlert from '@/composables/useAlert'
import { useAuthStore } from '@/stores'

// Route and session management
const route = useRoute()
const sessionId = computed(() => route.params.id)

// Auth store
const authStore = useAuthStore()

// Component refs
const countdownTimer = ref(null)

// Reactive data
const loading = ref(true)
const session = ref(null)
const error = ref(null)

// Video header state
const showVideoHeader = ref(true)
const countdown = ref(5)
let headerTimer = null
let headerCountdownTimer = null

// Quick join loading state
const joinLoading = ref(false)

// Computed properties
const isSessionEnded = computed(() => {
  return session.value?.is_ended === true
})

const isCurrentUserSpeaker = computed(() => {
  if (!authStore.user?.id || !session.value?.speakers) return false
  
  // Check if current user ID exists in the session speakers array
  return session.value.speakers.some(speaker => speaker.id === authStore.user.id)
})

const shouldShowIframe = computed(() => {
  const result = !isSessionEnded.value && 
    (session.value?.is_started || 
     countdownTimer.value?.videoStarted || 
     countdownTimer.value?.sessionStarted) && 
    !countdownTimer.value?.isSessionAboutToStart
  
  console.log('shouldShowIframe computed:', {
    isSessionEnded: isSessionEnded.value,
    sessionIsStarted: session.value?.is_started,
    videoStarted: countdownTimer.value?.videoStarted,
    sessionStarted: countdownTimer.value?.sessionStarted,
    isSessionAboutToStart: countdownTimer.value?.isSessionAboutToStart,
    result
  })
  
  return result
})

const shouldShowJoinPrompt = computed(() => {
  // Show join prompt when:
  // - Video should be showing (iframe time)
  // - User is not joined and not a speaker
  const result = shouldShowIframe.value && 
         !session.value?.is_joined && 
         !isCurrentUserSpeaker.value
  
  console.log('shouldShowJoinPrompt computed:', {
    shouldShowIframe: shouldShowIframe.value,
    isJoined: session.value?.is_joined,
    isCurrentUserSpeaker: isCurrentUserSpeaker.value,
    result
  })
  
  return result
})

const shouldShowVideoForJoinedUser = computed(() => {
  // Show video for joined users or speakers when it's video time
  // This helps hide overlays that shouldn't show for joined users
  const result = (session.value?.is_joined || isCurrentUserSpeaker.value) && 
                 (countdownTimer.value?.shouldStartVideo || 
                  countdownTimer.value?.videoStarted || 
                  countdownTimer.value?.sessionStarted ||
                  session.value?.is_started)
  
  console.log('shouldShowVideoForJoinedUser computed:', {
    isJoined: session.value?.is_joined,
    isCurrentUserSpeaker: isCurrentUserSpeaker.value,
    shouldStartVideo: countdownTimer.value?.shouldStartVideo,
    videoStarted: countdownTimer.value?.videoStarted,
    sessionStarted: countdownTimer.value?.sessionStarted,
    sessionIsStarted: session.value?.is_started,
    result
  })
  
  return result
})

const shouldShowVideoPlayer = computed(() => {
  // Show video player when:
  // 1. For joined users/speakers when video should start
  // 2. For non-joined users when shouldShowIframe is true but not showing join prompt (normal flow)
  const result = shouldShowVideoForJoinedUser.value || 
                 (shouldShowIframe.value && !shouldShowJoinPrompt.value)
  
  console.log('shouldShowVideoPlayer computed:', {
    shouldShowVideoForJoinedUser: shouldShowVideoForJoinedUser.value,
    shouldShowIframe: shouldShowIframe.value,
    shouldShowJoinPrompt: shouldShowJoinPrompt.value,
    result
  })
  
  return result
})

// Fetch session data
const fetchSession = async () => {
  if (!sessionId.value) {
    useAlert('No session ID provided', 'error')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await sessionApi.get(sessionId.value)
    
    if (response.data) {
      // Reset video state when new session data is loaded
      if (countdownTimer.value) {
        countdownTimer.value.resetVideoState()
      }
      
      session.value = response.data
    } else {
      throw new Error('Invalid session data received')
    }
  } catch (err) {
    console.error('Error fetching session:', err)
    error.value = err.message || 'Failed to load session'
    useAlert('Failed to load session details', 'error')
  } finally {
    loading.value = false
  }
}

// Video header management
const hideHeader = () => {
  showVideoHeader.value = false
}

const startHeaderTimer = () => {
  // Start countdown
  headerCountdownTimer = setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      clearInterval(headerCountdownTimer)
      hideHeader()
    }
  }, 1000)
  
  // Hide header after 5 seconds (simulating video start)
  headerTimer = setTimeout(() => {
    hideHeader()
  }, 5000)
}

// Event handlers
const handleSessionStarted = (data) => {
  console.log('Session started:', data)
  
  // Update session state
    if (session.value) {
      session.value.is_started = true
      session.value.is_live = true
    }
    
  // Show success message
    useAlert('Session is now live!', 'success')
    
    // Start the regular video header timer
    startHeaderTimer()
}

const handleCountdownUpdate = (newCountdown) => {
  console.log('Countdown update:', newCountdown)
}

const handleAboutToStart = (isAboutToStart) => {
  console.log('Session about to start:', isAboutToStart)
}

const handleTimeUntilStartUpdate = (timeUntilStart) => {
  console.log('Time until start update:', timeUntilStart)
}

const handleSessionJoined = (sessionId) => {
  console.log('Session joined:', sessionId)
  
      // Update local session state
  if (session.value) {
      session.value.is_joined = true
      if (session.value.attendees_count !== undefined) {
        session.value.attendees_count++
      }
  }
}

const handleSessionLeft = (sessionId) => {
  console.log('Session left:', sessionId)
  
      // Update local session state
  if (session.value) {
      session.value.is_joined = false
      if (session.value.attendees_count !== undefined && session.value.attendees_count > 0) {
        session.value.attendees_count--
      }
  }
}

const handleRateSession = (session) => {
  console.log('Rate session:', session)
  useAlert('Rating functionality not implemented yet', 'info')
}

const handleWriteReview = (session) => {
  console.log('Write review:', session)
  useAlert('Review functionality not implemented yet', 'info')
}

const handleLoginRequired = () => {
  console.log('Login required for session registration')
  useAlert('Please log in to register for this session', 'info')
  // You can implement redirection to login page here
  // router.push('/login')
}

const handleQuickJoin = async () => {
  if (!session.value?.id) {
    useAlert('Session ID not found', 'error')
    return
  }

  joinLoading.value = true
  
  try {
    // Call the join API (toggle endpoint)
    await join(session.value.id)
    
    const action = session.value.is_joined ? 'left' : 'joined'
    useAlert(`Successfully ${action} the session!`, 'success')
    
    // Update local session state
    if (session.value) {
      session.value.is_joined = !session.value.is_joined
      if (session.value.attendees_count !== undefined) {
        session.value.attendees_count += session.value.is_joined ? 1 : -1
      }
    }
  } catch (error) {
    console.error('Error joining session:', error)
    
    // Handle specific error cases
    if (error.response?.status === 422) {
      const errorMessage = error.response.data?.message || 'This session has already ended.'
      useAlert(errorMessage, 'warning')
    } else {
      useAlert('Failed to join session. Please try again.', 'error')
    }
  } finally {
    joinLoading.value = false
  }
}


// Lifecycle
onMounted(async () => {
  try {
    await fetchSession()
    
    // Start the regular timer when component mounts (only if session is loaded, not ended, and has started)
    if (session.value && !isSessionEnded.value && session.value.is_started) {
      startHeaderTimer()
    }
  } catch (error) {
    console.error('Error during component mount:', error)
    useAlert('Failed to initialize session', 'error')
  }
})

onUnmounted(() => {
  if (headerTimer) {
    clearTimeout(headerTimer)
  }
  if (headerCountdownTimer) {
    clearInterval(headerCountdownTimer)
  }
})
</script>

<style scoped>
/* Any remaining styles specific to the main component */
</style>